import React from 'react';
import { Send, X, CornerDownRight } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth-context';
import { createNotification } from '../../lib/notification-service';
import * as amplitude from '@amplitude/analytics-browser';
import confetti from 'canvas-confetti';

const playSendSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 1200;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch {}
};

interface MessageInputProps {
  threadCode: string;
  threadType: 'country' | 'general' | 'bug';
  userId: string;
  userEmail: string;
  replyingTo: { id: string; userEmail: string; username: string; userId: string } | null;
  onCancelReply: () => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onMessageSent?: (messageId: string) => void;
  soundEnabled?: boolean;
}

const MAX_MESSAGE_LENGTH = 2000;
const LINE_HEIGHT = 22;
const MAX_LINES = 6;
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES;

export function MessageInput({
  threadCode,
  threadType,
  userId,
  userEmail,
  replyingTo,
  onCancelReply,
  scrollContainerRef,
  onMessageSent,
  soundEnabled = true
}: MessageInputProps) {
  const { userInfo } = useAuth();
  const [content, setContent] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sendFlying, setSendFlying] = React.useState(false);
  const [messageCount, setMessageCount] = React.useState(() => {
    return parseInt(localStorage.getItem(`msg_count_${threadCode}`) || '0', 10);
  });
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const sendBtnRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
  }, [content]);

  React.useEffect(() => {
    if (replyingTo && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyingTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting || !userInfo?.user?.username) {
      setError('Impossible d\'envoyer le message. Veuillez reessayer.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    amplitude.track('Message Sent', {
      threadCode,
      threadType,
      isReply: !!replyingTo
    });

    try {
      const messageData = {
        content: content.trim(),
        user_id: userId,
        user_email: userEmail,
        username: userInfo.user.username,
        created_at: new Date(),
        likes: [],
        dislikes: [],
        status: 'active',
        thread_code: threadCode,
        thread_type: threadType,
        ...(replyingTo && {
          reply_to: {
            id: replyingTo.id,
            user_email: replyingTo.userEmail,
            username: replyingTo.username,
            user_id: replyingTo.userId
          }
        })
      };

      const docRef = await addDoc(collection(db, 'messages'), messageData);
      const newMessageId = docRef.id;

      setContent('');
      setSendFlying(true);
      setTimeout(() => setSendFlying(false), 400);
      if (soundEnabled) playSendSound();

      const newCount = messageCount + 1;
      setMessageCount(newCount);
      localStorage.setItem(`msg_count_${threadCode}`, String(newCount));

      if (newCount === 1 && sendBtnRef.current) {
        const rect = sendBtnRef.current.getBoundingClientRect();
        confetti({
          particleCount: 50,
          spread: 60,
          origin: {
            x: rect.left / window.innerWidth,
            y: rect.top / window.innerHeight
          },
          colors: ['#06b6d4', '#10b981', '#f59e0b', '#3b82f6'],
          gravity: 1.2,
          scalar: 0.8,
        });
      }

      if (replyingTo) {
        if (replyingTo.userId && replyingTo.userId !== userId) {
          createNotification({
            recipientId: replyingTo.userId,
            senderId: userId,
            senderUsername: userInfo.user.username,
            type: 'reply',
            messageId: newMessageId,
            messagePreview: content.trim(),
            threadCode,
            threadType,
          });
        }
        onCancelReply();
      }

      if (onMessageSent) {
        onMessageSent(newMessageId);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Une erreur est survenue lors de l\'envoi du message. Veuillez reessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const charPercent = content.length / MAX_MESSAGE_LENGTH;
  const circumference = 2 * Math.PI * 10;
  const strokeDashoffset = circumference * (1 - charPercent);
  const ringColor = charPercent > 0.9 ? '#ef4444' : charPercent > 0.75 ? '#f59e0b' : '#3b82f6';

  return (
    <div className="border-t border-gray-100 dark:border-gray-800/80 bg-white dark:bg-gray-950 px-4 sm:px-5 py-3">
      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 text-sm rounded-xl border border-red-100 dark:border-red-900/20 animate-scale-in">
          {error}
        </div>
      )}

      {replyingTo && (
        <div className="flex items-center gap-2 mb-2.5 px-3 py-2 bg-blue-50/80 dark:bg-blue-900/10 rounded-xl animate-scale-in">
          <CornerDownRight className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
          <span className="text-xs text-gray-500 dark:text-gray-400">Reponse a</span>
          <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
            {replyingTo.username || 'Anonyme'}
          </span>
          <button
            onClick={onCancelReply}
            className="ml-auto p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => {
                if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                  setContent(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  if (content.trim() && !isSubmitting) {
                    handleSubmit(e);
                  }
                }
              }}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder={replyingTo ? 'Ecrivez votre reponse... (Ctrl+Entree pour envoyer)' : 'Ecrivez votre message... (Ctrl+Entree pour envoyer)'}
              className="w-full px-4 py-2.5 pr-10 text-[13.5px] bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700/80 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white dark:focus:bg-gray-800 dark:text-white resize-none overflow-y-auto transition-all duration-200 placeholder:text-gray-400"
              style={{
                minHeight: `${LINE_HEIGHT * 1.5}px`,
                maxHeight: `${MAX_HEIGHT}px`,
                lineHeight: `${LINE_HEIGHT}px`,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              rows={1}
              required
              disabled={isSubmitting}
            />
            {content.length > 0 && (
              <div className="absolute right-3 bottom-2.5 transition-opacity duration-200">
                <svg width="24" height="24" viewBox="0 0 24 24" className="transform -rotate-90">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-200 dark:text-gray-700" />
                  <circle
                    cx="12" cy="12" r="10" fill="none" stroke={ringColor} strokeWidth="2" strokeLinecap="round"
                    strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-300"
                  />
                </svg>
                {charPercent > 0.9 && (
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-red-500 tabular-nums">
                    {MAX_MESSAGE_LENGTH - content.length}
                  </span>
                )}
              </div>
            )}
          </div>

          <button
            ref={sendBtnRef}
            type="submit"
            disabled={!content.trim() || isSubmitting}
            className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              content.trim()
                ? 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 text-white active:scale-95'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
            }`}
            title="Envoyer"
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className={`w-4 h-4 transition-transform duration-300 ${sendFlying ? 'animate-send-fly' : ''}`} />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
